import React, { useMemo } from "react";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Not recorded";
}

function getStart(shift) {
  return toDate(
    shift?.startTime ||
      shift?.startAt ||
      shift?.start
  );
}

function getEnd(shift) {
  return toDate(
    shift?.endTime ||
      shift?.endAt ||
      shift?.end
  );
}

function getAssignedWorkerId(shift) {
  return String(
    shift?.assignedWorkerUid ||
      shift?.workerUid ||
      shift?.workerId ||
      shift?.assignedWorkerUids?.[0] ||
      ""
  );
}

function InsightCard({
  title,
  description,
  tone = "neutral",
}) {
  const tones = {
    positive: {
      color: "#166534",
      background: "#f0fdf4",
      border: "#bbf7d0",
    },
    warning: {
      color: "#92400e",
      background: "#fffbeb",
      border: "#fde68a",
    },
    risk: {
      color: "#991b1b",
      background: "#fef2f2",
      border: "#fecaca",
    },
    neutral: {
      color: "#1e40af",
      background: "#eff6ff",
      border: "#bfdbfe",
    },
  };

  const style = tones[tone] || tones.neutral;

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 13,
        color: style.color,
        background: style.background,
        border: `1px solid ${style.border}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 950,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1.5,
        }}
      >
        {description}
      </div>
    </div>
  );
}

export default function Shift360AIInsights({
  shift,
  worker,
  facility,
}) {
  const analysis = useMemo(() => {
    if (!shift) {
      return {
        insights: [],
        actions: [],
        riskCount: 0,
      };
    }

    const status = normalize(
      shift?.status ||
        shift?.lifecycleStatus ||
        "open"
    );

    const start = getStart(shift);
    const end = getEnd(shift);
    const arrived = toDate(
      shift?.arrivedAt ||
        shift?.checkedInAt ||
        shift?.arrivalTime
    );

    const now = new Date();
    const assignedWorkerId =
      getAssignedWorkerId(shift);

    const insights = [];
    const actions = [];
    let riskCount = 0;

    const isClosed = [
      "completed",
      "cancelled",
      "canceled",
      "no_show",
      "closed_unfilled",
    ].includes(status);

    if (
      !assignedWorkerId &&
      !isClosed &&
      start &&
      start.getTime() > now.getTime()
    ) {
      const hoursUntilStart =
        (start.getTime() - now.getTime()) /
        3600000;

      const urgent = hoursUntilStart <= 24;

      insights.push({
        title: urgent
          ? "Urgent Coverage Gap"
          : "Unassigned Shift",
        description: `No worker is assigned. The shift begins in approximately ${Math.max(
          0,
          hoursUntilStart
        ).toFixed(1)} hours.`,
        tone: urgent ? "risk" : "warning",
      });

      actions.push(
        urgent
          ? "Immediately review qualified available workers and assignment conflicts."
          : "Assign a qualified and compliant worker before the activation window."
      );

      if (urgent) riskCount += 1;
    }

    if (assignedWorkerId && !isClosed) {
      insights.push({
        title: "Worker Assigned",
        description: `${
          worker?.displayName ||
          worker?.name ||
          shift?.assignedWorkerName ||
          "The selected worker"
        } is assigned to this shift.`,
        tone: "positive",
      });
    }

    if (
      shift?.isFlexibleTime === true ||
      shift?.timeRequired === true
    ) {
      insights.push({
        title: "Shift Time Required",
        description:
          "This flexible shift still requires confirmed start and end times.",
        tone: "warning",
      });

      actions.push(
        "Confirm the shift time before final assignment or activation."
      );
    }

    if (
      status === "assigned" &&
      start &&
      start.getTime() <= now.getTime()
    ) {
      insights.push({
        title: "Activation Review",
        description:
          "The scheduled start has arrived, but the shift remains assigned and has not been activated.",
        tone: "warning",
      });

      actions.push(
        "Contact the worker and confirm activation or attendance status."
      );
    }

    if (
      ["activated", "tracking"].includes(status) &&
      start &&
      now.getTime() >
        start.getTime() + 15 * 60 * 1000 &&
      !arrived
    ) {
      insights.push({
        title: "Arrival Delay Risk",
        description:
          "The worker has not recorded arrival more than 15 minutes after the scheduled start.",
        tone: "risk",
      });

      actions.push(
        "Contact the worker and evaluate whether no-show escalation is required."
      );

      riskCount += 1;
    }

    if (arrived && start) {
      const minutesFromStart =
        (arrived.getTime() - start.getTime()) /
        60000;

      if (minutesFromStart > 15) {
        insights.push({
          title: "Late Arrival Recorded",
          description: `Arrival was approximately ${Math.round(
            minutesFromStart
          )} minutes after the scheduled start.`,
          tone: "warning",
        });

        actions.push(
          "Review the late arrival with the worker and facility."
        );
      } else {
        insights.push({
          title: "Arrival On Time",
          description:
            "The recorded arrival was within 15 minutes of the scheduled start.",
          tone: "positive",
        });
      }
    }

    if (status === "no_show") {
      insights.push({
        title: "No-Show Incident",
        description:
          shift?.noShowReason ||
          "The assigned worker was marked as a no-show.",
        tone: "risk",
      });

      actions.push(
        "Confirm replacement coverage, document the incident, and review worker reliability."
      );

      riskCount += 1;
    }

    if (
      ["cancelled", "canceled"].includes(status)
    ) {
      insights.push({
        title: "Cancelled Shift",
        description:
          shift?.cancellationReason ||
          shift?.cancelReason ||
          "The shift was cancelled.",
        tone: "warning",
      });

      actions.push(
        "Verify the cancellation source and determine whether replacement coverage is required."
      );
    }

    if (status === "completed") {
      insights.push({
        title: "Shift Completed",
        description:
          shift?.autoCompleted === true
            ? "The shift was automatically completed after its scheduled end."
            : "The shift completed successfully.",
        tone: "positive",
      });

      const payrollStatus = normalize(
        shift?.payrollStatus
      );

      const invoiceStatus = normalize(
        shift?.invoiceStatus
      );

      if (
        ![
          "generated",
          "approved",
          "paid",
          "processed",
        ].includes(payrollStatus)
      ) {
        insights.push({
          title: "Payroll Pending",
          description:
            "The completed shift has not reached a processed payroll state.",
          tone: "warning",
        });

        actions.push(
          "Review and generate the payroll record."
        );
      }

      if (
        ![
          "generated",
          "sent",
          "paid",
          "funded",
        ].includes(invoiceStatus)
      ) {
        insights.push({
          title: "Invoice Pending",
          description:
            "The completed shift has not reached a generated invoice state.",
          tone: "warning",
        });

        actions.push(
          "Review and generate the facility invoice record."
        );
      }
    }

    if (!start || !end) {
      insights.push({
        title: "Incomplete Schedule Data",
        description:
          "The shift does not have a valid start and end time.",
        tone: "risk",
      });

      actions.push(
        "Correct the shift schedule before further lifecycle actions."
      );

      riskCount += 1;
    } else if (end <= start) {
      insights.push({
        title: "Invalid Shift Duration",
        description:
          "The recorded end time is not later than the start time.",
        tone: "risk",
      });

      actions.push(
        "Correct the start and end times immediately."
      );

      riskCount += 1;
    }

    if (
      status === "completed" &&
      !shift?.arrivedAt &&
      !shift?.checkedInAt
    ) {
      insights.push({
        title: "Missing Arrival Record",
        description:
          "The shift is completed but has no recorded arrival timestamp.",
        tone: "warning",
      });

      actions.push(
        "Verify attendance evidence before final payroll or invoice approval."
      );
    }

    if (insights.length === 0) {
      insights.push({
        title: "Normal Shift State",
        description:
          "No immediate operational risks were identified from the recorded shift data.",
        tone: "positive",
      });
    }

    if (actions.length === 0) {
      actions.push(
        "Continue normal lifecycle monitoring."
      );
    }

    return {
      status,
      start,
      end,
      assignedWorkerId,
      insights,
      actions: Array.from(new Set(actions)),
      riskCount,
    };
  }, [shift, worker]);

  if (!shift) return null;

  return (
    <div style={{ display: "grid", gap: 13 }}>
      <div
        style={{
          padding: 15,
          borderRadius: 16,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #312e81 0%, #7c3aed 100%)",
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 950,
          }}
        >
          AI Shift Insights
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#ede9fe",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          Operational assessment based on the shift’s
          current lifecycle and recorded evidence.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <div
          style={{
            padding: 12,
            borderRadius: 13,
            border: "1px solid #ddd6fe",
            background: "#faf5ff",
          }}
        >
          <div
            style={{
              color: "#64748b",
              fontSize: 9,
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            Risk Indicators
          </div>

          <div
            style={{
              marginTop: 5,
              color:
                analysis.riskCount > 0
                  ? "#b91c1c"
                  : "#166534",
              fontSize: 19,
              fontWeight: 950,
            }}
          >
            {analysis.riskCount}
          </div>
        </div>

        <div
          style={{
            padding: 12,
            borderRadius: 13,
            border: "1px solid #ddd6fe",
            background: "#faf5ff",
          }}
        >
          <div
            style={{
              color: "#64748b",
              fontSize: 9,
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            Facility
          </div>

          <div
            style={{
              marginTop: 5,
              color: "#6d28d9",
              fontSize: 13,
              fontWeight: 950,
              overflowWrap: "anywhere",
            }}
          >
            {String(
              facility?.facilityName ||
                facility?.name ||
                shift?.facilityName ||
                shift?.facilityId ||
                "Unavailable"
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <div
          style={{
            color: "#0f172a",
            fontSize: 13,
            fontWeight: 950,
          }}
        >
          Operational Assessment
        </div>

        {analysis.insights.map(
          (insight, index) => (
            <InsightCard
              key={`${insight.title}-${index}`}
              title={insight.title}
              description={insight.description}
              tone={insight.tone}
            />
          )
        )}
      </div>

      <div
        style={{
          padding: 13,
          borderRadius: 14,
          border: "1px solid #ddd6fe",
          background: "#faf5ff",
        }}
      >
        <div
          style={{
            color: "#6d28d9",
            fontSize: 13,
            fontWeight: 950,
          }}
        >
          Recommended Actions
        </div>

        <div
          style={{
            display: "grid",
            gap: 7,
            marginTop: 9,
            color: "#475569",
            fontSize: 10,
            fontWeight: 750,
            lineHeight: 1.5,
          }}
        >
          {analysis.actions.map(
            (action, index) => (
              <div key={`${action}-${index}`}>
                • {action}
              </div>
            )
          )}
        </div>
      </div>

      <div
        style={{
          padding: 11,
          borderRadius: 12,
          border: "1px solid #e2e8f0",
          background: "#f8fafc",
          color: "#64748b",
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        Shift start:{" "}
        {formatDateTime(analysis.start)}
        <br />
        Shift end: {formatDateTime(analysis.end)}
        <br />
        These insights provide decision support only.
        Administrators must verify worker credentials,
        availability, attendance evidence, facility
        requirements, and financial records before acting.
      </div>
    </div>
  );
}